

import { Id, OrganizationContact, ValidationErrors } from '../types';
import { abibApi, tags, methods } from './';

const apiOrganizationContacts = abibApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrganizationContacts: builder.query<{ items: OrganizationContact[] }, any>({
      query: ({ org_id }) => {
        return {
          url: `orgs/${org_id}/contacts`,
          method: methods.GET,
        };
      },
      providesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => ({
        items: data as OrganizationContact[],
      }),
    }),
    getOrganizationContact: builder.query<OrganizationContact, { org_id: Id, contact_id: Id }>({
      query: ({ org_id, contact_id }) => {
        return {
          url: `orgs/${org_id}/contacts/${contact_id}`,
          method: methods.GET,
        };
      },
      providesTags: (result, error, { org_id, contact_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => (data as OrganizationContact),
    }),
    createOrganizationContact: builder.mutation<OrganizationContact, { org_id: Id, contact: Partial<OrganizationContact> }>({
      query: ({ org_id, contact }) => {
        return {
          url: `orgs/${org_id}/contacts`,
          method: methods.POST,
          body: contact,
        };
      },
      invalidatesTags: (result, meta, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => (data as OrganizationContact),
    }),
    updateOrganizationContact: builder.mutation<OrganizationContact, { org_id: Id, contact_id: Id, contact: Partial<OrganizationContact> }>({
      query: ({ org_id, contact_id, contact }) => {
        return {
          url: `orgs/${org_id}/contacts/${contact_id}`,
          method: methods.PUT,
          body: contact,
        };
      },
      invalidatesTags: (result, meta, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => (data as OrganizationContact),
      transformErrorResponse: (error, meta, arg) : {status: -1} | {status: 422, errors: ValidationErrors<OrganizationContact>} => {
        if (error.status === 422) {
          const data = error.data as { errors: ValidationErrors<OrganizationContact> };
          return { status: 422, errors: data.errors };
        }
        return { status: -1 };
      },
    }),
    deleteOrganizationContact: builder.mutation<void, { org_id: Id, contact_id: Id }>({
      query: ({ org_id, contact_id }) => {
        return {
          url: `orgs/${org_id}/contacts/${contact_id}`,
          method: methods.DELETE,
        };
      },
      invalidatesTags: (result, meta, { org_id }) => [{ type: tags.organization, org_id }],
    }),
    makePrimaryOrganizationContact: builder.mutation<OrganizationContact, { org_id: Id, contact_id: Id }>({
      query: ({ org_id, contact_id }) => {
        return {
          url: `orgs/${org_id}/contacts/${contact_id}/make_primary`,
          method: methods.PUT,
        };
      },
      invalidatesTags: (result, meta, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => (data as OrganizationContact),
    }),
    removePrimaryOrganizationContact: builder.mutation<OrganizationContact, { org_id: Id, contact_id: Id }>({
      query: ({ org_id, contact_id }) => {
        return {
          url: `orgs/${org_id}/contacts/${contact_id}/remove_primary`,
          method: methods.PUT,
        };
      },
      invalidatesTags: (result, meta, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => (data as OrganizationContact),
    }),
  }),
  overrideExisting: false,
})


// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllOrganizationContactsQuery,
  useGetOrganizationContactQuery,
  useCreateOrganizationContactMutation,
  useUpdateOrganizationContactMutation,
  useDeleteOrganizationContactMutation,
  useMakePrimaryOrganizationContactMutation,
  useRemovePrimaryOrganizationContactMutation,
} = apiOrganizationContacts;
